import { FC, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ArrowRight, ArrowUpRight } from "lucide-react"
import { getStaffBriefing, Briefing } from "../../../api/staff-agent"

/**
 * "What's new for you" block in the assistant's empty state. Each item is a
 * single clickable row: the insight itself seeds a chat investigation (the
 * item's investigateChip is sent verbatim), and the optional trailing icon
 * opens the relevant admin page. Renders nothing while loading or on
 * failure — the static greeting copy underneath always stands on its own.
 */
interface BriefingGreetingProps {
  onInvestigate: (message: string) => void
}

const BriefingGreeting: FC<BriefingGreetingProps> = ({ onInvestigate }) => {
  const navigate = useNavigate()
  const [briefing, setBriefing] = useState<Briefing | null>(null)

  useEffect(() => {
    let mounted = true
    getStaffBriefing()
      .then(b => {
        if (mounted) setBriefing(b)
      })
      .catch(() => {
        /* greeting falls back to the static copy */
      })
    return () => {
      mounted = false
    }
  }, [])

  if (!briefing?.items?.length) return null

  return (
    <div className="w-full max-w-xl rounded-xl border border-violet-100 bg-violet-50/60 p-3 text-left">
      <p className="mb-1 px-2 text-xs font-semibold uppercase tracking-wide text-violet-700">
        What&apos;s new for you
      </p>
      <ul className="space-y-0.5">
        {briefing.items.map(item => (
          <li key={item.insight} className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onInvestigate(item.investigateChip)}
              title={item.investigateChip}
              className="group flex min-w-0 flex-1 items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-white"
            >
              <span className="text-sm text-neutral-700">{item.insight}</span>
              <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-violet-500 opacity-60 transition-opacity group-hover:opacity-100">
                Ask
                <ArrowRight size={12} />
              </span>
            </button>
            {item.platformLink && (
              <button
                type="button"
                onClick={() => navigate(item.platformLink!)}
                title="Open in admin"
                className="shrink-0 rounded-lg p-1.5 text-neutral-400 transition-colors hover:bg-white hover:text-violet-700"
              >
                <ArrowUpRight size={14} />
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

export default BriefingGreeting
